import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

// Import marker images as ES6 modules
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Utility function to format timestamps in Dublin timezone
const formatDublinTime = (timestamp) => {
  if (!timestamp) return 'Unknown';
  
  try {
    // Handle different timestamp formats
    let date;
    if (timestamp === 'Currently Connected') return timestamp;
    
    // Try parsing as ISO string or regular date string
    date = new Date(timestamp);
    
    // If invalid date, return original
    if (isNaN(date.getTime())) return timestamp;
    
    return date.toLocaleString("en-IE", {
      timeZone: "Europe/Dublin",
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error formatting timestamp:', timestamp, error);
    return timestamp;
  }
};

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const ListenerMap = ({ listeners }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapData, setMapData] = useState([]);
  // Removed loading state - data updates smoothly without overlays
  const [error, setError] = useState(null);
  const markersRef = useRef(new Map()); // Track markers by IP for smart updates

  // Fetch real-time listener map data from nginx logs
  const fetchMapData = async () => {
    try {
      // Smooth data updates without loading overlay
      setError(null);
      console.log('Fetching real-time listener map data from nginx logs...');
      
      const response = await axios.get(`/api/current-listeners?t=${Date.now()}`);
      
      // Extract listeners from response and convert to map format
      const responseData = response.data.success ? response.data.data : response.data;
      const listeners = responseData.listeners || [];
      
      // Convert listener data to map format with coordinates
      const mapData = listeners.map(listener => ({
        lat: listener.location?.lat,
        lon: listener.location?.lon,
        city: listener.location?.city || 'Unknown',
        country: listener.location?.country || 'Unknown',
        region: listener.location?.region || 'Unknown',
        isp: listener.location?.isp || 'Unknown',
        ip: listener.ip,
        stream: listener.stream,
        userAgent: listener.userAgent,
        timestamp: listener.timestamp
      })).filter(item => item.lat && item.lon && item.lat !== 0 && item.lon !== 0);
      
      setMapData(mapData);
      console.log(`Received ${mapData.length} real listener locations from nginx logs`);
    } catch (error) {
      console.error('Error fetching nginx map data:', error);
      setError(error.response?.data?.error || 'Failed to load real-time map data');
      setMapData([]);
    } finally {
      // Smooth data updates - no loading state needed
    }
  };

  // Fetch map data on component mount and set up auto-refresh
  useEffect(() => {
    fetchMapData();
    
    // Auto-refresh map data every 10 seconds to match other data sources
    const interval = setInterval(fetchMapData, 10000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    // Initialize map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        touchZoom: true,
      }).setView([20, 0], 2);

      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18,
      }).addTo(mapInstanceRef.current);
    }

    // Smart marker management - only update what changed
    const currentIPs = new Set(mapData.map(listener => listener.ip));
    const existingIPs = new Set(markersRef.current.keys());
    
    // Remove markers for disconnected listeners only
    existingIPs.forEach(ip => {
      if (!currentIPs.has(ip)) {
        const marker = markersRef.current.get(ip);
        if (marker) {
          mapInstanceRef.current.removeLayer(marker);
          markersRef.current.delete(ip);
          console.log(`🔴 Removed marker for disconnected listener: ${ip}`);
        }
      }
    });

    // Show error state
    if (error) {
      // Remove any existing "no listeners" message marker
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer.getIcon && layer.getIcon().options.className === 'custom-div-icon') {
          mapInstanceRef.current.removeLayer(layer);
        }
      });
      
      const statusDiv = L.divIcon({
        html: `
          <div style="
            background: var(--bg-secondary);
            color: var(--text-primary);
            padding: 1rem;
            border-radius: 8px;
            border: 1px solid var(--border);
            text-align: center;
            white-space: nowrap;
          ">
            ${error ? `Error: ${error}` : 'No listeners detected'}
          </div>
        `,
        className: 'custom-div-icon',
        iconSize: [300, 60],
        iconAnchor: [150, 30]
      });

      L.marker([20, 0], { icon: statusDiv }).addTo(mapInstanceRef.current);
      return;
    }

    // Use real-time map data instead of passed listeners
    const validListeners = mapData.filter(
      listener => 
        listener.lat && listener.lon && 
        listener.lat !== 0 && listener.lon !== 0
    );

    // Remove any existing "no listeners" message marker
    mapInstanceRef.current.eachLayer((layer) => {
      if (layer.getIcon && layer.getIcon().options.className === 'custom-div-icon') {
        mapInstanceRef.current.removeLayer(layer);
      }
    });

    if (validListeners.length === 0) {
      // Show message if no valid locations
      const noLocationDiv = L.divIcon({
        html: `
          <div style="
            background: var(--bg-secondary);
            color: var(--text-primary);
            padding: 1rem;
            border-radius: 8px;
            border: 1px solid var(--border);
            text-align: center;
            white-space: nowrap;
          ">
            No listeners with valid locations to display<br/>
            <span style="font-size: 0.8rem; opacity: 0.7;">Local connections (127.0.0.1) are filtered out</span>
          </div>
        `,
        className: 'custom-div-icon',
        iconSize: [300, 80],
        iconAnchor: [150, 40]
      });

      L.marker([20, 0], { icon: noLocationDiv }).addTo(mapInstanceRef.current);
      return;
    }

    // Add markers for new listeners only - keep existing ones stable
    validListeners.forEach((listener, index) => {
      const { lat, lon, ip } = listener;
      
      // Skip if marker already exists for this IP - keeps marker stable
      if (markersRef.current.has(ip)) {
        // Update existing marker popup with fresh data
        const existingMarker = markersRef.current.get(ip);
        const updatedPopupContent = `
          <div style="
            color: var(--text-primary);
            background: var(--bg-secondary);
            padding: 0.5rem;
            border-radius: 4px;
            min-width: 200px;
          ">
            <div style="font-weight: bold; margin-bottom: 0.5rem; color: var(--accent-primary);">
              Listener Details
            </div>
            <div style="margin-bottom: 0.25rem;">
              <strong>IP:</strong> <span style="font-family: monospace;">${ip}</span>
            </div>
            <div style="margin-bottom: 0.25rem;">
              <strong>Location:</strong> ${listener.city || 'Unknown'}, ${listener.region || 'Unknown'}, ${listener.country || 'Unknown'}
            </div>
            <div style="margin-bottom: 0.25rem;">
              <strong>User Agent:</strong> <span style="font-size: 0.8rem;">${listener.userAgent || 'Unknown'}</span>
            </div>
            <div style="margin-bottom: 0.25rem;">
              <strong>ISP:</strong> ${listener.isp || 'Unknown'}
            </div>
            <div style="margin-bottom: 0.25rem;">
              <strong>Stream:</strong> ${listener.stream || 'Unknown'}
            </div>
            <div>
              <strong>Connected At:</strong> <span style="color: var(--accent-secondary);">${formatDublinTime(listener.timestamp)}</span>
            </div>
          </div>
        `;
        existingMarker.setPopupContent(updatedPopupContent);
        return; // Skip creating new marker
      }
      
      // Create custom marker for NEW listener only
      const customIcon = L.divIcon({
        html: `
          <div style="
            background: var(--accent-primary);
            color: white;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          ">
            L
          </div>
        `,
        className: 'custom-listener-marker',
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });

      // Create popup content
      const popupContent = `
        <div style="
          color: var(--text-primary);
          background: var(--bg-secondary);
          padding: 0.5rem;
          border-radius: 4px;
          min-width: 200px;
        ">
          <div style="font-weight: bold; margin-bottom: 0.5rem; color: var(--accent-primary);">
            Listener Details
          </div>
          <div style="margin-bottom: 0.25rem;">
            <strong>IP:</strong> <span style="font-family: monospace;">${ip}</span>
          </div>
          <div style="margin-bottom: 0.25rem;">
            <strong>Location:</strong> ${listener.city || 'Unknown'}, ${listener.region || 'Unknown'}, ${listener.country || 'Unknown'}
          </div>
          <div style="margin-bottom: 0.25rem;">
            <strong>User Agent:</strong> <span style="font-size: 0.8rem;">${listener.userAgent || 'Unknown'}</span>
          </div>
          <div style="margin-bottom: 0.25rem;">
            <strong>ISP:</strong> ${listener.isp || 'Unknown'}
          </div>
          <div style="margin-bottom: 0.25rem;">
            <strong>Stream:</strong> ${listener.stream || 'Unknown'}
          </div>
          <div>
            <strong>Connected At:</strong> <span style="color: var(--accent-secondary);">${formatDublinTime(listener.timestamp)}</span>
          </div>
        </div>
      `;

      // Create and add new marker
      const marker = L.marker([lat, lon], { icon: customIcon })
        .bindPopup(popupContent, {
          maxWidth: 300,
          className: 'custom-popup'
        })
        .addTo(mapInstanceRef.current);
      
      // Track this marker by IP for future updates
      markersRef.current.set(ip, marker);
      console.log(`🟢 Added new marker for listener: ${ip} at ${listener.city}, ${listener.country}`);
    });

    // Fit map to show all markers (only if we have markers)
    if (markersRef.current.size > 0) {
      const group = new L.featureGroup(Array.from(markersRef.current.values()));
      mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1));
    }

  }, [mapData, error]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      // Clear marker tracking
      markersRef.current.clear();
    };
  }, []);

  return (
    <div className="map-container">
      <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
      
      {/* Map Legend */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '0.75rem',
        fontSize: '0.875rem',
        color: 'var(--text-primary)',
        zIndex: 1000
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
          Real-Time Listener Map
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{
            background: 'var(--accent-primary)',
            color: 'white',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px'
          }}>
            L
          </span>
          <span>Active Listener</span>
        </div>
        <div style={{ 
          fontSize: '0.75rem', 
          color: 'var(--text-secondary)', 
          marginBottom: '0.25rem'
        }}>
          Click markers for details
        </div>
        <div style={{ 
          fontSize: '0.7rem', 
          color: 'var(--text-secondary)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          <span>{mapData.length} real IPs • Updates every 10s</span>
        </div>
      </div>
    </div>
  );
};

export default ListenerMap;